import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FC, useState } from "react";

import { Button, Headline, Small } from "@fronterahealth/frontera-ui-components";

import { customFetch } from "@api/rest/custom-fetcher";
import { AddCriteriaForm } from "@pages/QASettings/AddCriteriaForm";
import { BaseCriteriaCard } from "@pages/QASettings/BaseCriteriaCard";

// Types matching the expected REST response shape for base criteria endpoints
interface BaseQACriteriaItem {
  id: number;
  name: string;
  category: string;
  content: string;
}

export const BaseCriteriaSettings: FC = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["base-qa-criteria"],
    queryFn: () =>
      customFetch<{ data: { results: BaseQACriteriaItem[] }; status: number }>(`/api/basecamp/criteria/base`),
  });

  const { mutateAsync: createCriteria, isPending: isCreating } = useMutation({
    mutationFn: (args: { name: string; content: string; category: string }) =>
      customFetch(`/api/basecamp/criteria/base/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args),
      }),
    onSuccess: () => {
      setShowAddForm(false);
      void queryClient.invalidateQueries({ queryKey: ["base-qa-criteria"] });
    },
  });

  const { mutateAsync: updateCriteria } = useMutation({
    mutationFn: (args: { id: number; name?: string; content?: string; category?: string }) =>
      customFetch(`/api/basecamp/criteria/base/update`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args),
      }),
    onSuccess: () => {
      void refetch();
    },
  });

  const allCriteria = data?.status === 200 ? (data.data.results ?? []) : [];

  const handleCreate = async (name: string, category: string, content: string) => {
    await createCriteria({ name, content, category });
    setShowAddForm(false);
    void refetch();
  };

  const handleNameSave = async (id: string, name: string) => {
    await updateCriteria({ id: Number(id), name });
    void refetch();
  };

  const handleContentSave = async (id: string, content: string) => {
    await updateCriteria({ id: Number(id), content });
    void refetch();
  };

  const handleCategorySave = async (id: string, category: string) => {
    await updateCriteria({ id: Number(id), category });
    void refetch();
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center rounded-lg border border-dashed border-gray-300 p-12">
        <Small colorType="secondary">Loading...</Small>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full flex-col content-between items-center lg:flex-col lg:items-start px-16 py-8">
      <div className="mt-4 flex w-full items-center justify-between">
        <Headline size="medium">Base QA Criteria</Headline>
        <Button
          appearance="primary"
          onClick={() => setShowAddForm((prev) => !prev)}
          text={showAddForm ? "Cancel" : "Add Criteria"}
        />
      </div>

      {showAddForm && (
        <AddCriteriaForm isSubmitting={isCreating} onSubmit={handleCreate} onCancel={() => setShowAddForm(false)} />
      )}

      {isFetching && !isLoading && (
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          <Small colorType="secondary">Refreshing...</Small>
        </div>
      )}

      <div className="mt-6 flex w-full flex-col gap-4">
        {allCriteria.length > 0 ? (
          allCriteria.map((criteria) => {
            if (!criteria) return null;
            return (
              <BaseCriteriaCard
                key={criteria.id}
                criteria={{
                  id: String(criteria.id),
                  name: criteria.name,
                  category: criteria.category,
                  content: criteria.content,
                }}
                onNameSave={handleNameSave}
                onContentSave={handleContentSave}
                onCategorySave={handleCategorySave}
              />
            );
          })
        ) : (
          <div className="flex w-full items-center justify-center rounded-lg border border-dashed border-gray-300 p-12">
            <Small colorType="secondary">No base criteria yet. Use "Add Criteria" to create one.</Small>
          </div>
        )}
      </div>
    </div>
  );
};
